/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import "./IdleTimeout.css";

// Idle-timeout warning, extracted from the Admin dashboard's pattern: after
// `timeoutMs` without user activity, show a modal with a countdown; the user
// either stays signed in (resets the timer) or is logged out cleanly through
// the provided callback. This exists because the backend session expiring on
// its own surfaces as a raw 401, which the app answers by wiping auth state
// and reloading mid-action — the "platform keeps logging me out" experience
// an org reported. Warning first, clean logout after, is the fix.

const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // sign out after 30 minutes idle
const DEFAULT_WARNING_SECONDS = 60; // warn this many seconds before signing out
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
];

const FALLBACKS = {
  idleWarningTitle: "You've been inactive",
  idleWarningMessage:
    "For your security, you'll be signed out due to inactivity in:",
  staySignedIn: "Stay signed in",
  signOutNow: "Sign out now",
};

class IdleTimeout extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showWarning: false,
      countdown: this.warningSeconds(),
    };
    this.resetIdleTimer = this.resetIdleTimer.bind(this);
    this.onStaySignedIn = this.onStaySignedIn.bind(this);
    this.onIdleLogout = this.onIdleLogout.bind(this);
  }

  componentDidMount() {
    this.resetIdleTimer();
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, this.resetIdleTimer)
    );
  }

  componentWillUnmount() {
    ACTIVITY_EVENTS.forEach((evt) =>
      window.removeEventListener(evt, this.resetIdleTimer)
    );
    clearTimeout(this._warnTimer);
    clearTimeout(this._logoutTimer);
    clearInterval(this._countdownInterval);
  }

  t = (key) => {
    const block =
      this.props.languageObj && typeof this.props.languageObj === "object"
        ? this.props.languageObj
        : {};
    return block[key] || FALLBACKS[key];
  };

  timeoutMs = () => this.props.timeoutMs || DEFAULT_TIMEOUT_MS;
  warningSeconds = () => this.props.warningSeconds || DEFAULT_WARNING_SECONDS;

  resetIdleTimer() {
    // Once the warning is showing, only the explicit "stay signed in" click
    // (which also calls this) should reset it — ambient mouse movement
    // shouldn't silently dismiss a warning nobody has actually noticed.
    if (this.state.showWarning) return;

    clearTimeout(this._warnTimer);
    clearTimeout(this._logoutTimer);

    this._warnTimer = setTimeout(() => {
      this.setState({
        showWarning: true,
        countdown: this.warningSeconds(),
      });
      this._countdownInterval = setInterval(() => {
        this.setState((prevState) => {
          if (prevState.countdown <= 1) {
            clearInterval(this._countdownInterval);
            return { countdown: 0 };
          }
          return { countdown: prevState.countdown - 1 };
        });
      }, 1000);
    }, this.timeoutMs() - this.warningSeconds() * 1000);

    this._logoutTimer = setTimeout(this.onIdleLogout, this.timeoutMs());
  }

  onStaySignedIn() {
    clearInterval(this._countdownInterval);
    this.setState({ showWarning: false }, this.resetIdleTimer);
  }

  onIdleLogout() {
    clearInterval(this._countdownInterval);
    this.setState({ showWarning: false });
    this.props.onLogout();
  }

  render() {
    if (!this.state.showWarning) return null;
    return (
      <div className="IdleTimeout-overlay">
        <div className="IdleTimeout-modal">
          <h3>{this.t("idleWarningTitle")}</h3>
          <p>{this.t("idleWarningMessage")}</p>
          <div className="IdleTimeout-countdown">{this.state.countdown}s</div>
          <div className="IdleTimeout-actions">
            <button
              type="button"
              className="IdleTimeout-btn"
              onClick={this.onIdleLogout}
            >
              {this.t("signOutNow")}
            </button>
            <button
              type="button"
              className="IdleTimeout-btn IdleTimeout-btnPrimary"
              onClick={this.onStaySignedIn}
            >
              {this.t("staySignedIn")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

IdleTimeout.propTypes = {
  onLogout: PropTypes.func.isRequired,
  languageObj: PropTypes.object,
  timeoutMs: PropTypes.number,
  warningSeconds: PropTypes.number,
};

export default IdleTimeout;
